// ==========================================================================
// Phone capture service — set the authenticated user's phone number.
// Backend endpoints (E13 + security follow-up):
//   POST /api/user/phone/send-code              — request an OTP for a phone
//   POST /api/user/phone                        — verify code + persist
//   POST /api/user/phone/from-telegram-contact  — Telegram-signed payload
//
// The manual path is two-step (send-code → verify) so an attacker can't
// squat someone else's phone via this endpoint and ride a magic-link
// login into their account.
// ==========================================================================

import api from '@/lib/axios';
import type { User } from '@/types/auth.types';
import { AxiosError } from 'axios';

interface SetPhoneResponse {
  user: User;
}

interface SendCodeResponse {
  expires_in: number;
}

export class PhoneTakenException extends Error {
  constructor() {
    super('phone_taken');
    this.name = 'PhoneTakenException';
  }
}

export class InvalidPhoneCodeException extends Error {
  constructor() {
    super('invalid_code');
    this.name = 'InvalidPhoneCodeException';
  }
}

export class PhoneCodeRateLimitedException extends Error {
  public readonly cooldown: number;
  constructor(cooldown: number) {
    super('rate_limited');
    this.name = 'PhoneCodeRateLimitedException';
    this.cooldown = cooldown;
  }
}

/**
 * Step 1 — ask the backend to text an OTP to phoneNumber.
 * Throws PhoneTakenException if the number is already owned by someone else
 * (saves a wasted SMS round-trip), PhoneCodeRateLimitedException with
 * cooldown seconds on a 429, and rethrows other errors.
 */
export async function requestPhoneCode(phoneNumber: string): Promise<SendCodeResponse> {
  try {
    const response = await api.post<SendCodeResponse>('/user/phone/send-code', {
      phone_number: phoneNumber,
    });
    return response.data;
  } catch (err) {
    if (err instanceof AxiosError && err.response) {
      const data = err.response.data as { code?: string; cooldown?: number } | null;
      if (err.response.status === 422 && data?.code === 'phone_taken') {
        throw new PhoneTakenException();
      }
      if (err.response.status === 429 && data?.code === 'rate_limited') {
        throw new PhoneCodeRateLimitedException(Number(data.cooldown ?? 0));
      }
    }
    throw err;
  }
}

/**
 * Step 2 — verify the OTP and persist phoneNumber on the authenticated
 * user. Throws InvalidPhoneCodeException for wrong/expired codes,
 * PhoneTakenException if someone else claimed it between send and verify.
 */
export async function setPhoneNumber(phoneNumber: string, code: string): Promise<User> {
  try {
    const response = await api.post<SetPhoneResponse>('/user/phone', {
      phone_number: phoneNumber,
      code,
    });
    return response.data.user;
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 422) {
      const data = err.response.data as { code?: string } | null;
      if (data?.code === 'phone_taken') {
        throw new PhoneTakenException();
      }
      if (data?.code === 'invalid_code') {
        throw new InvalidPhoneCodeException();
      }
    }
    throw err;
  }
}

/**
 * Status of the latest WebApp.requestContact() share for this user.
 * `pending` means the bot webhook hasn't recorded an outcome yet (or
 * the cache entry expired); the Mini-App polls until it sees a
 * terminal value. `saved` is the success path — caller should refresh
 * the user profile.
 */
export type TelegramShareStatus = 'pending' | 'saved' | 'phone_taken' | 'user_mismatch';

export interface TelegramShareStatusResponse {
  status: TelegramShareStatus;
  at?: string;
}

export async function getTelegramShareStatus(): Promise<TelegramShareStatusResponse> {
  const response = await api.get<TelegramShareStatusResponse>('/user/phone/share-status');
  return response.data;
}
